<script setup lang="ts">
  import { computed, onMounted, ref } from "vue";
  import { storeToRefs } from "pinia";
  import { IndexPageLayout, IndexPageFilters } from "@/components/App/IndexPage";
  import { useJcdBenchmarksStore } from "@stores/jcdBenchmarks";
  import { useToastsStore } from "@stores/toastsStore";
  import JcdBenchmarksTable from "./JcdBenchmarksTable.vue";
  import JcdBenchmarkFormDialog from "./JcdBenchmarkFormDialog.vue";
  import type { JcdBenchmark, JcdBenchmarkPayload, JcdBenchmarkWithStats } from "@/types";

  const store = useJcdBenchmarksStore();
  const toast = useToastsStore();
  const { benchmarks, isLoading } = storeToRefs(store);

  const searchQuery = ref("");
  const isFormOpen = ref(false);
  const editedBenchmark = ref<JcdBenchmark | null>(null);

  onMounted(() => store.fetch());

  const filteredBenchmarks = computed(() =>
    benchmarks.value.filter((benchmark) => benchmark.name.toLowerCase().includes(searchQuery.value.toLowerCase())),
  );

  const openCreate = () => {
    editedBenchmark.value = null;
    isFormOpen.value = true;
  };

  const openEdit = (benchmark: JcdBenchmarkWithStats) => {
    editedBenchmark.value = benchmark;
    isFormOpen.value = true;
  };

  const handleSubmit = async (payload: JcdBenchmarkPayload) => {
    const saved = editedBenchmark.value
      ? await store.update(editedBenchmark.value.id, payload)
      : await store.create(payload);
    if (saved) {
      isFormOpen.value = false;
      toast.success(editedBenchmark.value ? "Benchmark enregistré" : "Benchmark créé");
    }
  };

  const handleDuplicate = async (benchmark: JcdBenchmarkWithStats) => {
    if (await store.duplicate(benchmark.id)) toast.success("Benchmark dupliqué");
  };

  const handleDelete = async (benchmark: JcdBenchmarkWithStats) => {
    if (await store.destroy(benchmark.id)) toast.success("Benchmark supprimé");
  };
</script>

<template>
  <IndexPageLayout
    title="Benchmarks JCDecaux"
    action-label="Nouveau benchmark"
    @action="openCreate">
    <template #filters>
      <IndexPageFilters
        v-model:search-model-value="searchQuery"
        search-placeholder="Rechercher un benchmark" />
    </template>

    <JcdBenchmarksTable
      :benchmarks="filteredBenchmarks"
      :loading="isLoading"
      @edit="openEdit"
      @duplicate="handleDuplicate"
      @delete="handleDelete" />

    <template #dialogs>
      <JcdBenchmarkFormDialog
        v-model:open="isFormOpen"
        :benchmark="editedBenchmark"
        @submit="handleSubmit" />
    </template>
  </IndexPageLayout>
</template>
